import { useMutation } from "@tanstack/react-query";
import { portalApiClient } from "../apiClient";
import { apiUrls } from "src/consts/ApiUrls";
import { queryClient } from "src/main";
import { queryKeys } from "../queryKeys";
import { useParams } from "react-router-dom";
import { uploadFileToBlob } from "../uploadFileToBlob";
import { SasFileResult } from "src/types/InspectionUpdateRequest";
import { usePosition } from "./usePosition";

export default function useFileMutation(
  onUploadProgress?: (progress: number, fileName: string) => void
) {
  const position = usePosition();
  const { companyId } = useParams();
  const uploadFileMutation = useMutation({
    mutationFn: (payload: SasFileResult): Promise<unknown> => {
      if (onUploadProgress) {
        if (process.env.VITE_NODE_ENV === "cypress") {
          onUploadProgress(1, payload.fileName);
          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(true);
            }, 1000);
          });
        }
        return uploadFileToBlob(payload, onUploadProgress);
      }
      return uploadFileToBlob(payload);
    },
    mutationKey: ["file", "blob", "upload"],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["Files"],
      });
    },
  });

  const createFileEntityMutation = useMutation({
    mutationFn: async (fileObject: Omit<SasFileResult, "files" | "url">) => {
      const data = {
        ...fileObject,
        file: undefined,
        fileName: fileObject.file?.name,
        position: {
          latitude: position.latitude || 0,
          longitude: position.longitude || 0,
          adress:
            (document.getElementById("title") as HTMLInputElement)?.value ||
            ".",
        },
      };
      try {
        const result = await portalApiClient.post<SasFileResult>(
          apiUrls.file.create.url(),
          data
        );

        return {
          url: result.data.files?.[0] || "",
          file: fileObject.file,
          id: result.data.id,
          companyId: Number(companyId),
          comment: fileObject.comment,
          depiction: fileObject.depiction,
          fileName: fileObject.fileName,
          files: result.data.files,
          exif: fileObject.exif,
          boundingBox: fileObject.boundingBox,
          deviationId: fileObject.deviationId,
          imageResponse: result.data.imageResponse,
          fileType: fileObject.fileType,
          position: result.data.position || undefined,
          groupId: fileObject.groupId,
        } as SasFileResult;
      } catch (error) {
        console.error("Error in createFileEntityMutation: ", error);
      }
    },
    mutationKey: ["file", "entity", "create"],
    onSuccess: (data, variables) => {
      if (data?.file && !variables.id) {
        uploadFileMutation.mutate(data);
      }
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.files(Number(companyId!), Number(data?.id)),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.files(companyId!, "all"),
        });
      }
    },
  });

  return {
    create: createFileEntityMutation,
    upload: uploadFileMutation,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: ["Files"],
      }),
  };
}
